import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/projects'

/**
 * Flag swap service for previewing and creating temporary swaps (overlays)
 * from a source environment onto a target environment (ZEN-764). A swap
 * replaces the whole target with a snapshot of the source's targeting
 * (ZEN-2030), so it is chosen by environment, not flag by flag.
 */
class FlagSwapService {
    /**
     * Preview what a swap would change on the target: each flag published on
     * the source, with the value, rule count and active rollout on both sides
     * and whether the flag would serve differently.
     *
     * @param {string} projectKey - Project key
     * @param {string} targetEnvironmentKey - Target environment key
     * @param {string} sourceEnvironmentUlid - Source environment ULID
     * @returns {Promise<Array<Object>>} Flag diff rows
     */
    async preview(projectKey, targetEnvironmentKey, sourceEnvironmentUlid) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    targetEnvironmentKey +
                    '/swaps/preview',
                { params: { source_environment_ulid: sourceEnvironmentUlid } }
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Create a temporary swap onto the target environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} targetEnvironmentKey - Target environment key
     * @param {Object} form - Swap payload: source_environment_ulid and expires_at
     * @returns {Promise<Object>} Created overlay
     */
    async create(projectKey, targetEnvironmentKey, form) {
        return await apiClient
            .post(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    targetEnvironmentKey +
                    '/swaps',
                form
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * List the temporary swaps still applying on the target environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} targetEnvironmentKey - Target environment key
     * @returns {Promise<Object>} Response body with the active overlays
     */
    async list(projectKey, targetEnvironmentKey) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    targetEnvironmentKey +
                    '/swaps'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Load what the target serves for one flag while a whole-environment swap
     * runs: the source's targets for the flag with their values, rules and
     * rollout, as they were when the swap was created. Rejects with a 404 when
     * no snapshot swap serves the flag.
     *
     * @param {string} projectKey - Project key
     * @param {string} targetEnvironmentKey - Target environment key
     * @param {string} flagKey - Flag key
     * @returns {Promise<Object>} Response body: overlay, source_environment and targets
     */
    async served(projectKey, targetEnvironmentKey, flagKey) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    targetEnvironmentKey +
                    '/swaps/flags/' +
                    flagKey
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Load one page of the audit history of swaps applied to the target
     * environment, newest first (ZEN-767).
     *
     * @param {string} projectKey - Project key
     * @param {string} targetEnvironmentKey - Target environment key
     * @param {Object} params - Optional actor_ulid, created_from, created_to, limit and cursor
     * @returns {Promise<Object>} Body with the page of events, the actors who appear in the history, and paging meta
     */
    async history(projectKey, targetEnvironmentKey, params = {}) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    targetEnvironmentKey +
                    '/swaps/history',
                { params }
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Revert an active swap now, restoring the target's published values.
     *
     * @param {string} projectKey - Project key
     * @param {string} targetEnvironmentKey - Target environment key
     * @param {string} overlayUlid - ULID of the overlay to revert
     * @returns {Promise<Object>} Response body with the reverted overlay
     */
    async revert(projectKey, targetEnvironmentKey, overlayUlid) {
        return await apiClient
            .post(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    targetEnvironmentKey +
                    '/swaps/' +
                    overlayUlid +
                    '/revert'
            )
            .then((response) => {
                return response.data
            })
    }
}

export default new FlagSwapService()
